import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import useAuth from "hooks/useAuth";
import Input from "components/ui/Input";
import logo from "assets/img/logo.png";

// Invalid Company Component (no hooks)
const InvalidCompanyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full text-center">
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="mb-6">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.726-.833-2.498 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Invalid Access
            </h3>
            <p className="text-gray-600 text-sm">
              Company ID is missing or invalid. Please contact your administrator for the correct login URL.
            </p>
          </div>
          <div className="mt-6">
            <button
              onClick={() => window.location.href = '/'}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 shadow-lg"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Login Component (with hooks)
const MainLogin = ({ companyId }) => {
  const { login, isLoggingIn } = useAuth();
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
    companyId: companyId,
  });
  const [showPassword, setShowPassword] = useState(false);

  const updateCredentials = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogin = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (
      !credentials.username ||
      !credentials.password ||
      credentials.companyId === "null"
    ) {
      return;
    }

    console.log("handleLogin called");
    // Call the login function from useAuth hook
    login({
      ...credentials,
      clientTimestamp: new Date().toISOString(),
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleLogin(e);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Logo and Header */}
          <div className="text-center">
            <img
              className="h-16 w-auto mx-auto mb-6"
              src={logo}
              alt="Company Logo"
            />
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-xl shadow-2xl p-8">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 text-center">
                Sign in to {process.env.REACT_APP_BRAND_NAME || "Business Management System"}
              </h3>
              <p className="text-gray-600 text-sm text-center mt-1">
                Enter your credentials to access the system
              </p>
            </div>
            
            <form className="space-y-5" onKeyPress={handleKeyPress}>
              <Input
                id="username"
                name="username"
                type="text"
                label="Username"
                value={credentials.username}
                onChange={updateCredentials}
                placeholder="Enter your username"
                autoComplete="username"
                isRequired
              />

              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  value={credentials.password}
                  onChange={updateCredentials}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  isRequired
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-[34px] text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Login Button */}
              <button
                type="button"
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                onClick={handleLogin}
                disabled={isLoggingIn}
              >
                {isLoggingIn ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Authenticating...
                  </div>
                ) : (
                  "Access System"
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-blue-200 text-xs">
              © 2024 {process.env.REACT_APP_BRAND_NAME || "Business Management System"}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
  );
};

// Main Login wrapper component
const Login = () => {
  const { id } = useParams();

  // Check if company ID is invalid - render separate component without hooks
  if (!id || id === "null" || id === "undefined") {
    return <InvalidCompanyPage />;
  }

  // Render main login with hooks
  return <MainLogin companyId={id} />;
};

export default Login;
